import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import api, { formatApiError } from "../api";
import { useAuth } from "../AuthContext";
import { EXPERTISE, CONTRACT_TYPES, PROVINCES } from "../lib/constants";

export default function AanmeldenKandidaat() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", password: "", phone: "", linkedin_url: "",
    address: "", postcode: "", city: "",
    expertise_categories: [], years_experience: 0, available_from: "",
    hourly_rate_min: "", hourly_rate_max: "",
    salary_min: "", salary_max: "",
    contract_type: "Beide", province_preference: [], motivation: "",
    newsletter_subscribed: true, consent: false,
  });
  const [resume, setResume] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleProvince = (p) => {
    setForm((f) => ({
      ...f,
      province_preference: f.province_preference.includes(p)
        ? f.province_preference.filter((x) => x !== p)
        : [...f.province_preference, p],
    }));
  };
  const toggleExpertise = (x) => {
    setForm((f) => ({
      ...f,
      expertise_categories: f.expertise_categories.includes(x)
        ? f.expertise_categories.filter((y) => y !== x)
        : [...f.expertise_categories, x],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Geef je AVG-toestemming.");
    if (form.expertise_categories.length === 0) return toast.error("Kies minstens één expertisegebied.");
    if (!resume) return toast.error("CV (PDF) is verplicht.");
    setBusy(true);
    try {
      const payload = {
        ...form,
        years_experience: Number(form.years_experience) || 0,
        hourly_rate_min: form.hourly_rate_min ? Number(form.hourly_rate_min) : null,
        hourly_rate_max: form.hourly_rate_max ? Number(form.hourly_rate_max) : null,
        salary_min: form.salary_min ? Number(form.salary_min) : null,
        salary_max: form.salary_max ? Number(form.salary_max) : null,
      };
      const { data } = await api.post("/auth/register-candidate", payload);
      setUser(data);
      const fd = new FormData();
      fd.append("file", resume);
      try {
        await api.post("/resume/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch (_) { /* non-blocking */ }
      toast.success("Welkom! Je account is aangemaakt.");
      nav("/kandidaat-dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Aanmelding mislukt");
    }
    setBusy(false);
  };

  return (
    <section className="ok-section">
      <div className="ok-container grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="ok-eyebrow mb-3">Voor kandidaten</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]">
            Aanmelden als kandidaat.
          </h1>
          <p className="mt-5 text-[#4a5d5e] leading-relaxed">
            Maak je profiel aan, upload je cv en wij koppelen je aan passende opdrachten bij overheidsorganisaties.
          </p>
          <div className="mt-8 p-5 bg-[#f7f6f2] border border-[#e2e8f0] text-sm text-[#4a5d5e]">
            <strong className="text-[#0a191a]">Privacy:</strong> je gegevens worden alleen gebruikt om je te matchen op opdrachten. Wij delen niets zonder jouw toestemming.
          </div>
          <p className="mt-6 text-sm">Al een account? <Link to="/login" className="text-[#01696f] font-semibold">Inloggen</Link></p>
        </div>

        <form onSubmit={submit} className="lg:col-span-8 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Voornaam" required>
              <input data-testid="reg-first-name" required className="ok-input" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} />
            </Field>
            <Field label="Achternaam" required>
              <input data-testid="reg-last-name" required className="ok-input" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} />
            </Field>
            <Field label="E-mailadres" required>
              <input data-testid="reg-email" required type="email" className="ok-input" value={form.email} onChange={(e) => set("email", e.target.value)} />
            </Field>
            <Field label="Wachtwoord" required>
              <input data-testid="reg-password" required type="password" minLength={8} className="ok-input" value={form.password} onChange={(e) => set("password", e.target.value)} />
            </Field>
            <Field label="Telefoonnummer">
              <input data-testid="reg-phone" className="ok-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </Field>
            <Field label="LinkedIn URL (optioneel)">
              <input data-testid="reg-linkedin" className="ok-input" placeholder="https://linkedin.com/in/…" value={form.linkedin_url} onChange={(e) => set("linkedin_url", e.target.value)} />
            </Field>
            <Field label="Jaren ervaring">
              <input data-testid="reg-years" type="number" min={0} className="ok-input" value={form.years_experience} onChange={(e) => set("years_experience", e.target.value)} />
            </Field>
            <Field label="Beschikbaar vanaf">
              <input data-testid="reg-available" type="date" className="ok-input" value={form.available_from} onChange={(e) => set("available_from", e.target.value)} />
            </Field>
            <Field label="Gewenst dienstverband" required>
              <select data-testid="reg-contract" className="ok-input" value={form.contract_type} onChange={(e) => set("contract_type", e.target.value)}>
                {CONTRACT_TYPES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
          </div>

          {(form.contract_type === "ZZP" || form.contract_type === "Beide") && (
            <div className="border border-[#e2e8f0] p-5 bg-[#f7f6f2]">
              <div className="ok-eyebrow mb-3">ZZP tariefrange (per uur)</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Minimum (€/uur)">
                  <input data-testid="reg-rate-min" type="number" step="0.5" className="ok-input" placeholder="bv. 85" value={form.hourly_rate_min} onChange={(e) => set("hourly_rate_min", e.target.value)} />
                </Field>
                <Field label="Maximum (€/uur)">
                  <input data-testid="reg-rate-max" type="number" step="0.5" className="ok-input" placeholder="bv. 110" value={form.hourly_rate_max} onChange={(e) => set("hourly_rate_max", e.target.value)} />
                </Field>
              </div>
            </div>
          )}
          {(form.contract_type === "Detacheren/dienstverband" || form.contract_type === "Beide") && (
            <div className="border border-[#e2e8f0] p-5 bg-[#f7f6f2]">
              <div className="ok-eyebrow mb-3">Detacheren / dienstverband salarisrange (bruto per maand)</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Minimum (€/maand)">
                  <input data-testid="reg-salary-min" type="number" className="ok-input" placeholder="bv. 4500" value={form.salary_min} onChange={(e) => set("salary_min", e.target.value)} />
                </Field>
                <Field label="Maximum (€/maand)">
                  <input data-testid="reg-salary-max" type="number" className="ok-input" placeholder="bv. 6500" value={form.salary_max} onChange={(e) => set("salary_max", e.target.value)} />
                </Field>
              </div>
            </div>
          )}

          <div className="border border-[#e2e8f0] p-5">
            <div className="ok-eyebrow mb-3">Adresgegevens (NAW)</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Straat en huisnummer">
                <input data-testid="reg-address" className="ok-input" value={form.address} onChange={(e) => set("address", e.target.value)} />
              </Field>
              <Field label="Postcode">
                <input data-testid="reg-postcode" className="ok-input" value={form.postcode} onChange={(e) => set("postcode", e.target.value)} />
              </Field>
              <Field label="Woonplaats">
                <input data-testid="reg-city" className="ok-input" value={form.city} onChange={(e) => set("city", e.target.value)} />
              </Field>
            </div>
          </div>

          <Field label="Expertisegebied (meerdere mogelijk)" required>
            <div className="flex flex-wrap gap-2">
              {EXPERTISE.map((x) => {
                const active = form.expertise_categories.includes(x);
                return (
                  <button
                    key={x}
                    type="button"
                    data-testid={`reg-expertise-${x}`}
                    onClick={() => toggleExpertise(x)}
                    className={`px-3 py-1.5 text-xs border rounded-sm transition-colors ${active ? "bg-[#01696f] text-white border-[#01696f]" : "bg-white border-[#e2e8f0] hover:border-[#01696f]"}`}
                  >
                    {x}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label="Provincievoorkeur (meerdere mogelijk)">
            <div className="flex flex-wrap gap-2">
              {PROVINCES.map((p) => {
                const active = form.province_preference.includes(p);
                return (
                  <button
                    key={p}
                    type="button"
                    data-testid={`reg-province-${p}`}
                    onClick={() => toggleProvince(p)}
                    className={`px-3 py-1.5 text-xs border rounded-sm transition-colors ${active ? "bg-[#01696f] text-white border-[#01696f]" : "bg-white border-[#e2e8f0] hover:border-[#01696f]"}`}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label="Korte motivatie">
            <textarea data-testid="reg-motivation" rows={4} className="ok-input" value={form.motivation} onChange={(e) => set("motivation", e.target.value)} />
          </Field>

          <Field label="CV (PDF, optioneel)">
            <label className="flex items-center gap-3 p-4 border border-dashed border-[#01696f] bg-[#e6f0f1]/40 cursor-pointer hover:bg-[#e6f0f1]">
              <Upload size={18} className="text-[#01696f]" />
              <span className="text-sm">{resume ? resume.name : "Klik om een PDF te uploaden"}</span>
              <input data-testid="reg-resume" type="file" accept="application/pdf" className="hidden" onChange={(e) => setResume(e.target.files?.[0] || null)} />
            </label>
          </Field>

          <label className="flex items-start gap-3 text-sm">
            <input data-testid="reg-newsletter" type="checkbox" className="mt-1" checked={form.newsletter_subscribed} onChange={(e) => set("newsletter_subscribed", e.target.checked)} />
            <span>Ik wil nieuwsbrieven ontvangen met passende opdrachten.</span>
          </label>
          <label className="flex items-start gap-3 text-sm">
            <input data-testid="reg-consent" type="checkbox" className="mt-1" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} />
            <span>Ik ga akkoord met de verwerking van mijn persoonsgegevens conform de AVG.</span>
          </label>

          <button data-testid="reg-submit" disabled={busy} className="bg-[#01696f] text-white px-8 py-3 rounded-sm hover:bg-[#015257] disabled:opacity-60 font-medium">
            {busy ? "Bezig…" : "Aanmelden"}
          </button>
        </form>
      </div>

      <style>{`
        .ok-input { width: 100%; padding: 0.625rem 0.75rem; border: 1px solid #e2e8f0; background: #fff; font-size: 0.875rem; border-radius: 2px; }
        .ok-input:focus { outline: 2px solid #01696f; outline-offset: 1px; }
      `}</style>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider font-semibold text-[#4a5d5e] mb-2">
        {label}{required && <span className="text-[#d32f2f]"> *</span>}
      </label>
      {children}
    </div>
  );
}
